import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { OnboardingFacade } from '../../../Facades/onboarding.facade';
import { ONBOARDING_SESSION_RESOLVED_STATE } from '../../../Services/onboarding-resume.service';
import { Onboarding } from '../../../Models/onboarding.model';
import { OnboardingStatus } from '../../../Enums/onboarding-status.enum';
import { ALREADY_COMPLETED_KEY, RENEWAL_NOT_PERMITTED_KEY } from '../../../Consts/onboarding-error-keys.const';

function buildCompletedRedirect(router: Router): UrlTree {
  return router.createUrlTree(['/blocked'], { queryParams: { reason: 'completed' } });
}

function buildRenewalRedirect(router: Router): UrlTree {
  return router.createUrlTree(['/blocked'], { queryParams: { reason: 'renewal' } });
}

function conflictMessage(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return null;
  }

  return typeof error.error?.message === 'string' ? error.error.message : null;
}

function redirectForConflict(router: Router, error: unknown): UrlTree | true {
  switch (conflictMessage(error)) {
    case ALREADY_COMPLETED_KEY:
      return buildCompletedRedirect(router);
    case RENEWAL_NOT_PERMITTED_KEY:
      return buildRenewalRedirect(router);
    default:
      return true;
  }
}

function resolveSession(router: Router, onboardingFacade: OnboardingFacade): Observable<Onboarding> {
  const alreadyResolved = router.currentNavigation()?.extras.state?.[ONBOARDING_SESSION_RESOLVED_STATE] === true;
  const cached = onboardingFacade.onboarding();

  return alreadyResolved && cached ? of(cached) : onboardingFacade.createSession();
}

export const onboardingCompletedGuard: CanActivateFn = () => {
  const onboardingFacade = inject(OnboardingFacade);
  const router = inject(Router);

  return resolveSession(router, onboardingFacade).pipe(
    map(onboarding => (onboarding.status === OnboardingStatus.COMPLETED ? buildCompletedRedirect(router) : true)),
    catchError((error: unknown) => of(redirectForConflict(router, error))),
  );
};
